// src/utils/patientContactValidation.js
//
// Validações de contato do paciente (email e CPF) compartilhadas entre
// o funil (EmailForm.jsx, CpfForm.jsx) e o fallback em ConsultaChat.jsx.

export const KNOWN_EMAIL_DOMAINS = [
  'gmail.com', 'hotmail.com', 'outlook.com', 'yahoo.com', 'yahoo.com.br',
  'hotmail.com.br', 'outlook.com.br', 'icloud.com', 'live.com', 'msn.com',
  'uol.com.br', 'bol.com.br', 'terra.com.br', 'globo.com', 'ig.com.br',
  'protonmail.com', 'zoho.com', 'aol.com', 'mail.com', 'gmx.com',
  'r7.com', 'zipmail.com.br', 'oi.com.br', 'globomail.com',
  'live.com.br', 'me.com', 'mac.com', 'pm.me', 'tutanota.com',
];

const levenshtein = (a, b) => {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
  return dp[m][n];
};

/**
 * Considera "quase igual" para UX de confirmação de e-mail (typos curtos).
 * Limite sobe levemente com e-mails longos (provedor corporativo).
 */
export const emailsLookLikeTypoPair = (a, b) => {
  const x = String(a || '').trim().toLowerCase();
  const y = String(b || '').trim().toLowerCase();
  if (!x || !y) return false;
  if (x === y) return true;
  const d = levenshtein(x, y);
  const maxTypo = Math.min(3, Math.max(2, Math.floor(Math.min(x.length, y.length) / 12)));
  return d > 0 && d <= maxTypo;
};

/**
 * Escolhe qual candidato está mais próximo do texto de referência (ex.: 3ª digitação).
 * Retorna null se empate alto ou distâncias grandes nas duas pontas.
 */
export const pickCloserEmailCandidate = (reference, candidateA, candidateB) => {
  const ref = String(reference || '').trim().toLowerCase();
  const a = String(candidateA || '').trim().toLowerCase();
  const b = String(candidateB || '').trim().toLowerCase();
  if (!ref || !a || !b) return null;
  const da = levenshtein(ref, a);
  const db = levenshtein(ref, b);
  const threshold = Math.min(3, Math.max(2, Math.floor(ref.length / 12)));
  const aOk = da <= threshold;
  const bOk = db <= threshold;
  if (aOk && !bOk) return 'a';
  if (bOk && !aOk) return 'b';
  if (aOk && bOk && da !== db) return da < db ? 'a' : 'b';
  return null;
};

/**
 * Detecta domínio conhecido com "lixo" grudado no final, sem separador de label.
 * Ex.: "gmail.comccc" → "gmail.com", "hotmail.como" → "hotmail.com",
 * "outlook.com.brr" → "outlook.com.br".
 *
 * Diferente do typo por Levenshtein, este caso passa quando o excesso tem mais
 * de 2 caracteres (ex.: "ccc"), por isso é tratado por prefixo. Para evitar
 * falsos positivos em domínios legítimos mais longos (ex.: "mail.company.com",
 * que começa com "mail.com"), só consideramos quando o excesso NÃO contém ponto
 * — ou seja, foi colado diretamente ao último label do domínio conhecido.
 * Retorna o domínio conhecido correto, ou null.
 */
export const matchKnownDomainWithGluedSuffix = (domain) => {
  const d = String(domain || '').trim().toLowerCase();
  if (!d || KNOWN_EMAIL_DOMAINS.includes(d)) return null;
  let best = null;
  for (const known of KNOWN_EMAIL_DOMAINS) {
    if (!d.startsWith(known)) continue;
    const extra = d.slice(known.length);
    if (!extra || extra.includes('.')) continue;
    // Prefere o domínio conhecido mais longo (melhor sugestão).
    if (!best || known.length > best.length) best = known;
  }
  return best;
};

export const suggestEmailDomain = (email) => {
  const trimmed = String(email || '').trim().toLowerCase();
  const atIdx = trimmed.lastIndexOf('@');
  if (atIdx < 1) return null;
  const domain = trimmed.slice(atIdx + 1);
  if (!domain || KNOWN_EMAIL_DOMAINS.includes(domain)) return null;

  let bestDomain = null;
  let bestDist = Infinity;
  for (const known of KNOWN_EMAIL_DOMAINS) {
    const dist = levenshtein(domain, known);
    if (dist > 0 && dist <= 2 && dist < bestDist) {
      bestDist = dist;
      bestDomain = known;
    }
  }
  if (!bestDomain) bestDomain = matchKnownDomainWithGluedSuffix(domain);
  return bestDomain ? trimmed.slice(0, atIdx + 1) + bestDomain : null;
};

export const isLikelyTypoDomain = (email) => {
  const e = String(email || '').trim().toLowerCase();
  const atIdx = e.lastIndexOf('@');
  if (atIdx < 1) return false;
  const domain = e.slice(atIdx + 1);
  if (!domain || KNOWN_EMAIL_DOMAINS.includes(domain)) return false;
  if (matchKnownDomainWithGluedSuffix(domain)) return true;
  for (const known of KNOWN_EMAIL_DOMAINS) {
    if (levenshtein(domain, known) <= 2) return true;
  }
  return false;
};

/**
 * Validade de forma do e-mail. NÃO reprova domínio "parecido com conhecido":
 * `isLikelyTypoDomain` serve para SUGERIR a correção na UI, nunca para barrar.
 * Misturar as duas coisas criava beco sem saída no cadastro — @vivo.com.br,
 * @tim.com.br, @bb.com.br, @email.com, @yahoo.com.mx e @protonmail.ch (todos
 * reais) ficavam com o botão "Continuar" desabilitado, e o alerta "Você quis
 * dizer…?" — que tem o escape "Não, está correto" — só aparece no submit, que
 * o botão desabilitado nunca permitia.
 */
export const isValidEmail = (value) => {
  const e = String(value || '').trim().toLowerCase();
  if (!e || e.length > 254) return false;
  const parts = e.split('@');
  if (parts.length !== 2) return false;
  const [local, domain] = parts;
  if (!local || local.length > 64) return false;
  if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) return false;
  if (/[^a-z0-9.!#$%&'*+/=?^_`{|}~-]/.test(local)) return false;
  if (!domain || domain.length > 253) return false;
  if (domain.startsWith('-') || domain.endsWith('-')) return false;
  if (domain.startsWith('.') || domain.endsWith('.') || domain.includes('..')) return false;
  if (!/^[a-z0-9.-]+$/.test(domain)) return false;
  const domainParts = domain.split('.');
  if (domainParts.length < 2) return false;
  const tld = domainParts[domainParts.length - 1];
  if (tld.length < 2 || /^\d+$/.test(tld)) return false;
  for (const p of domainParts) {
    if (!p || p.length > 63 || p.startsWith('-') || p.endsWith('-')) return false;
  }
  return true;
};

export const formatCpf = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

export const isValidCpf = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(digits[i]) * (10 - i);
  let remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== Number(digits[9])) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) sum += Number(digits[i]) * (11 - i);
  remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== Number(digits[10])) return false;

  return true;
};
